import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Send } from 'lucide-react';
import { companyInfo } from '../../mock';
import Footer from '../../components/Footer';
import { db } from '../../lib/firebaseClient';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'sonner';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'contactSubmissions'), {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        projectIdea: formData.message,
        createdAt: serverTimestamp(),
        status: 'pending',
        source: 'contact-us-page'
      });
      
      toast.success('Message Sent!', {
        description: "Thank you for reaching out. We'll get back to you soon.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Submission Failed', {
        description: error.message || "Failed to send message. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-dark)' }}>
      {/* Navigation */}
      <nav className="border-b" style={{ borderColor: 'rgba(9, 166, 163, 0.2)', backgroundColor: 'rgba(36, 37, 38, 0.95)' }}>
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-16 w-auto" />
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-6xl">
        <Link to="/" className="inline-flex items-center gap-2 text-accent-teal hover:text-white transition-colors duration-300 mb-8">
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Contact Us</h1>
        <p className="text-lg text-neutral-silver mb-12">We'd love to hear from you. Get in touch with our team.</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Get In Touch</h2>
            <p className="text-neutral-silver mb-8">
              Have a question or want to discuss a project? Reach out to us through any of the following channels.
            </p>

            <div className="space-y-6">
              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(9, 166, 163, 0.1)' }}>
                  <Mail size={24} className="text-accent-teal" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Email</h3>
                  <a href="mailto:support@ntstudios.com" className="text-neutral-silver hover:text-accent-teal transition-colors">
                    support@ntstudios.com
                  </a>
                  <p className="text-sm text-neutral-silver mt-1">We'll respond within 24 hours</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(9, 166, 163, 0.1)' }}>
                  <Phone size={24} className="text-accent-teal" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Phone</h3>
                  <a href="tel:+911234567890" className="text-neutral-silver hover:text-accent-teal transition-colors">
                    +91 123 456 7890
                  </a>
                  <p className="text-sm text-neutral-silver mt-1">Mon-Fri, 9 AM - 6 PM IST</p>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(9, 166, 163, 0.1)' }}>
                  <MapPin size={24} className="text-accent-teal" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Location</h3>
                  <p className="text-neutral-silver">
                    India<br />
                    Remote-first studio
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-12 p-6 rounded-lg" style={{ backgroundColor: 'rgba(109, 90, 137, 0.1)', border: '1px solid rgba(109, 90, 137, 0.3)' }}>
              <h3 className="text-white font-semibold mb-3">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/tools" className="text-accent-teal hover:underline">Explore AI Tools</Link>
                </li>
                <li>
                  <Link to="/#projects" className="text-accent-teal hover:underline">View Our Work</Link>
                </li>
                <li>
                  <Link to="/#about" className="text-accent-teal hover:underline">About NT Studios</Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="p-8 rounded-2xl" style={{ backgroundColor: 'rgba(109, 90, 137, 0.1)', border: '1px solid rgba(109, 90, 137, 0.3)' }}>
              <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-white font-medium mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      backgroundColor: 'rgba(109, 90, 137, 0.1)',
                      borderColor: 'rgba(109, 90, 137, 0.3)',
                      color: 'white',
                    }}
                    className="w-full px-4 py-3 rounded-lg border text-white placeholder-neutral-silver/50 focus:outline-none focus:border-accent-teal transition-colors"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-white font-medium mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      backgroundColor: 'rgba(109, 90, 137, 0.1)',
                      borderColor: 'rgba(109, 90, 137, 0.3)',
                      color: 'white',
                    }}
                    className="w-full px-4 py-3 rounded-lg border text-white placeholder-neutral-silver/50 focus:outline-none focus:border-accent-teal transition-colors"
                    placeholder="john@example.com"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-white font-medium mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    style={{
                      backgroundColor: 'rgba(109, 90, 137, 0.1)',
                      borderColor: 'rgba(109, 90, 137, 0.3)',
                      color: 'white',
                    }}
                    className="w-full px-4 py-3 rounded-lg border text-white placeholder-neutral-silver/50 focus:outline-none focus:border-accent-teal transition-colors"
                    placeholder="Project inquiry"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-white font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    style={{
                      backgroundColor: 'rgba(109, 90, 137, 0.1)',
                      borderColor: 'rgba(109, 90, 137, 0.3)',
                      color: 'white',
                    }}
                    className="w-full px-4 py-3 rounded-lg border text-white placeholder-neutral-silver/50 focus:outline-none focus:border-accent-teal transition-colors resize-none"
                    placeholder="Tell us about your project or question..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-lg bg-accent-teal text-white font-semibold hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUs;
